import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Plus, Search, Factory, ArrowRight, ArrowLeft, Clock, CheckCircle, AlertTriangle
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
    Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROCESOS = [
    { value: 'preparacion', label: 'Preparación' },
    { value: 'acabado_delantero', label: 'Acabado Delantero' },
    { value: 'ensamble', label: 'Ensamble' },
    { value: 'traseras', label: 'Traseras' },
    { value: 'completo', label: 'Completo' }
];

const ESTADOS_TRABAJO = [
    { value: 'bueno', label: 'Bueno' },
    { value: 'regular', label: 'Regular' },
    { value: 'malo', label: 'Malo' }
];

export const Maquila = () => {
    const [registros, setRegistros] = useState([]);
    const [cortes, setCortes] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [lotes, setLotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [entregaModalOpen, setEntregaModalOpen] = useState(false);
    const [retornoModalOpen, setRetornoModalOpen] = useState(false);
    const [selectedEntrega, setSelectedEntrega] = useState(null);

    const getEmpleadoFoto = (id) => {
        const emp = empleados.find(e => e.id === id);
        return emp?.foto_url || '';
    };

    const EmpAvatar = ({ nombre, empleadoId }) => {
        const foto = getEmpleadoFoto(empleadoId);
        const initials = nombre ? nombre.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() : '?';
        if (foto) return <img src={`${API_URL}${foto}`} alt={nombre} className="w-7 h-7 rounded-full object-cover border border-zinc-700 shrink-0" />;
        return <div className="w-7 h-7 rounded-full bg-amber-400/15 flex items-center justify-center text-[10px] font-bold text-amber-400 shrink-0">{initials}</div>;
    };

    const [entregaForm, setEntregaForm] = useState({
        fecha: new Date().toISOString().split('T')[0],
        lote_id: '',
        maquilador_id: '',
        maquilador_nombre: '',
        maquilador_telefono: '',
        proceso: '',
        corte_id: '',
        cantidad_entregada: 0,
        entrega_nombre: '',
        observaciones: ''
    });

    const [retornoForm, setRetornoForm] = useState({
        entrega_id: '',
        fecha: new Date().toISOString().split('T')[0],
        cantidad_regresada: 0,
        cantidad_faltante: 0,
        incidencias: '',
        estado_trabajo: '',
        recibe_nombre: '',
        observaciones: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [maquilaRes, cortesRes, empRes, lotesRes] = await Promise.all([
                axios.get(`${API_URL}/api/maquila`),
                axios.get(`${API_URL}/api/cortes`),
                axios.get(`${API_URL}/api/empleados`),
                axios.get(`${API_URL}/api/lotes`)
            ]);
            setRegistros(maquilaRes.data);
            setCortes(cortesRes.data);
            setEmpleados(empRes.data);
            setLotes(lotesRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const maquiladores = empleados.filter(e => 
        e.tipo_trabajador === 'maquilador' || e.puesto?.toLowerCase().includes('maquilador')
    );

    const handleMaquiladorChange = (empId) => {
        const emp = empleados.find(e => e.id === empId);
        setEntregaForm(prev => ({
            ...prev,
            maquilador_id: empId,
            maquilador_nombre: emp ? emp.nombre : '',
            maquilador_telefono: emp ? emp.telefono : ''
        }));
    };

    const handleCorteChange = (corteId) => {
        const corte = cortes.find(c => c.id === corteId);
        setEntregaForm(prev => ({
            ...prev,
            corte_id: corteId,
            lote_id: corte ? corte.lote_id : ''
        }));
    };

    const handleEntregaSubmit = async (e) => {
        e.preventDefault();
        if (!entregaForm.maquilador_id || !entregaForm.proceso || !entregaForm.corte_id || !entregaForm.entrega_nombre) {
            toast.error('Por favor complete los campos requeridos');
            return;
        }

        try {
            await axios.post(`${API_URL}/api/maquila/entrega`, entregaForm);
            toast.success('Entrega registrada exitosamente');
            setEntregaModalOpen(false);
            setEntregaForm({
                fecha: new Date().toISOString().split('T')[0],
                lote_id: '', maquilador_id: '', maquilador_nombre: '',
                maquilador_telefono: '', proceso: '', corte_id: '',
                cantidad_entregada: 0, entrega_nombre: '', observaciones: ''
            });
            fetchData();
        } catch (error) {
            console.error('Error saving entrega:', error);
            toast.error(error.response?.data?.detail || 'Error al registrar entrega');
        }
    };

    const handleRetornoSubmit = async (e) => {
        e.preventDefault();
        if (!retornoForm.estado_trabajo || !retornoForm.recibe_nombre) {
            toast.error('Por favor complete los campos requeridos');
            return;
        }

        try {
            await axios.post(`${API_URL}/api/maquila/retorno`, retornoForm);
            toast.success('Retorno registrado exitosamente');
            setRetornoModalOpen(false);
            setSelectedEntrega(null);
            fetchData();
        } catch (error) {
            console.error('Error saving retorno:', error);
            toast.error(error.response?.data?.detail || 'Error al registrar retorno');
        }
    };

    const openRetornoModal = (entrega) => {
        setSelectedEntrega(entrega);
        setRetornoForm({
            entrega_id: entrega.id,
            fecha: new Date().toISOString().split('T')[0],
            cantidad_regresada: entrega.cantidad_entregada,
            cantidad_faltante: 0,
            incidencias: '',
            estado_trabajo: '',
            recibe_nombre: '',
            observaciones: ''
        });
        setRetornoModalOpen(true);
    };

    const getProcesoLabel = (proceso) => {
        const found = PROCESOS.find(p => p.value === proceso);
        return found ? found.label : proceso;
    };

    const getCorteRelacion = (corteId) => {
        const corte = cortes.find(c => c.id === corteId);
        return corte ? corte.relacion_corte : '-';
    };

    const getEstadoClass = (estado) => {
        switch (estado) {
            case 'completado': return 'badge-success';
            case 'con_faltantes': return 'badge-error';
            default: return 'badge-warning';
        }
    };

    const pendientes = registros.filter(r => r.estado === 'pendiente');
    const completados = registros.filter(r => r.estado !== 'pendiente');

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="maquila-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100" >
                        Control de Maquila
                    </h1>
                    <p className="text-zinc-400 mt-1">Gestión de entregas y retornos de maquila</p>
                </div>
                <Button
                    onClick={() => {
                        setEntregaForm({
                            fecha: new Date().toISOString().split('T')[0],
                            lote_id: '', maquilador_id: '', maquilador_nombre: '',
                            maquilador_telefono: '', proceso: '', corte_id: '',
                            cantidad_entregada: 0, entrega_nombre: '', observaciones: ''
                        });
                        setEntregaModalOpen(true);
                    }}
                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900"
                    data-testid="add-entrega-btn"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Nueva Entrega
                </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-yellow-400/10 flex items-center justify-center">
                            <Clock className="w-6 h-6 text-yellow-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">{pendientes.length}</p>
                            <p className="text-sm text-zinc-400">Pendientes</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-400/10 flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">
                                {completados.filter(r => r.estado === 'completado').length}
                            </p>
                            <p className="text-sm text-zinc-400">Completados</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-red-400/10 flex items-center justify-center">
                            <AlertTriangle className="w-6 h-6 text-red-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">
                                {completados.filter(r => r.estado === 'con_faltantes').length}
                            </p>
                            <p className="text-sm text-zinc-400">Con Faltantes</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Tabs defaultValue="pendientes" className="w-full">
                <TabsList className="mb-4">
                    <TabsTrigger value="pendientes" data-testid="tab-pendientes">
                        Pendientes ({pendientes.length})
                    </TabsTrigger>
                    <TabsTrigger value="historial" data-testid="tab-historial">
                        Historial ({completados.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="pendientes">
                    <Card className="card">
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-zinc-800">
                                            <TableHead className="table-header">Fecha</TableHead>
                                            <TableHead className="table-header">Maquilador</TableHead>
                                            <TableHead className="table-header">Proceso</TableHead>
                                            <TableHead className="table-header">Corte</TableHead>
                                            <TableHead className="table-header">Cantidad</TableHead>
                                            <TableHead className="table-header">Estado</TableHead>
                                            <TableHead className="table-header text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {pendientes.length > 0 ? pendientes.map((registro) => (
                                            <TableRow key={registro.id} className="table-row" data-testid={`maquila-row-${registro.id}`}>
                                                <TableCell className="table-cell">{registro.fecha}</TableCell>
                                                <TableCell className="table-cell font-medium">
                                                    <div className="flex items-center gap-2">
                                                        <EmpAvatar nombre={registro.maquilador_nombre} empleadoId={registro.maquilador_id} />
                                                        {registro.maquilador_nombre}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    <span className="badge-info">{getProcesoLabel(registro.proceso)}</span>
                                                </TableCell>
                                                <TableCell className="table-cell">{getCorteRelacion(registro.corte_id)}</TableCell>
                                                <TableCell className="table-cell">{registro.cantidad_entregada}</TableCell>
                                                <TableCell className="table-cell">
                                                    <span className="badge-warning">Pendiente</span>
                                                </TableCell>
                                                <TableCell className="table-cell text-right">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => openRetornoModal(registro)}
                                                        className="bg-emerald-500 hover:bg-emerald-600"
                                                        data-testid={`registrar-retorno-${registro.id}`}
                                                    >
                                                        <ArrowLeft className="w-4 h-4 mr-1" />
                                                        Retorno
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow>
                                                <TableCell colSpan={7} className="text-center py-8 text-zinc-400">
                                                    No hay entregas pendientes
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="historial">
                    <Card className="card">
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-zinc-800">
                                            <TableHead className="table-header">Fecha</TableHead>
                                            <TableHead className="table-header">Maquilador</TableHead>
                                            <TableHead className="table-header">Proceso</TableHead>
                                            <TableHead className="table-header">Entregado</TableHead>
                                            <TableHead className="table-header">Regresado</TableHead>
                                            <TableHead className="table-header">Faltantes</TableHead>
                                            <TableHead className="table-header">Estado</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {completados.length > 0 ? completados.map((registro) => (
                                            <TableRow key={registro.id} className="table-row">
                                                <TableCell className="table-cell">{registro.fecha}</TableCell>
                                                <TableCell className="table-cell font-medium">
                                                    <div className="flex items-center gap-2">
                                                        <EmpAvatar nombre={registro.maquilador_nombre} empleadoId={registro.maquilador_id} />
                                                        {registro.maquilador_nombre}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    <span className="badge-info">{getProcesoLabel(registro.proceso)}</span>
                                                </TableCell>
                                                <TableCell className="table-cell">{registro.cantidad_entregada}</TableCell>
                                                <TableCell className="table-cell">{registro.cantidad_regresada || '-'}</TableCell>
                                                <TableCell className="table-cell">
                                                    {registro.cantidad_faltante > 0 ? (
                                                        <span className="text-red-400 font-medium">{registro.cantidad_faltante}</span>
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell className="table-cell">
                                                    <span className={getEstadoClass(registro.estado)}>
                                                        {registro.estado === 'completado' ? 'Completado' : 'Con Faltantes'}
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        )) : (
                                            <TableRow>
                                                <TableCell colSpan={7} className="text-center py-8 text-zinc-400">
                                                    No hay registros en el historial
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Entrega Modal */}
            <Dialog open={entregaModalOpen} onOpenChange={setEntregaModalOpen}>
                <DialogContent className="sm:max-w-[550px]" data-testid="entrega-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <ArrowRight className="w-5 h-5 inline mr-2 text-amber-400" />
                            Nueva Entrega a Maquila
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEntregaSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Fecha *</Label>
                                <Input
                                    type="date"
                                    value={entregaForm.fecha}
                                    onChange={(e) => setEntregaForm({ ...entregaForm, fecha: e.target.value })}
                                    data-testid="entrega-fecha"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Cantidad *</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={entregaForm.cantidad_entregada}
                                    onChange={(e) => setEntregaForm({ ...entregaForm, cantidad_entregada: parseInt(e.target.value) || 0 })}
                                    data-testid="entrega-cantidad"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Maquilador *</Label>
                            <Select value={entregaForm.maquilador_id} onValueChange={handleMaquiladorChange}>
                                <SelectTrigger data-testid="entrega-maquilador">
                                    <SelectValue placeholder="Seleccionar maquilador" />
                                </SelectTrigger>
                                <SelectContent>
                                    {(maquiladores.length > 0 ? maquiladores : empleados).map(e => (
                                        <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Proceso *</Label>
                                <Select value={entregaForm.proceso} onValueChange={(v) => setEntregaForm({ ...entregaForm, proceso: v })}>
                                    <SelectTrigger data-testid="entrega-proceso">
                                        <SelectValue placeholder="Seleccionar" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PROCESOS.map((p) => (
                                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Corte *</Label>
                                <Select value={entregaForm.corte_id} onValueChange={handleCorteChange}>
                                    <SelectTrigger data-testid="entrega-corte">
                                        <SelectValue placeholder="Seleccionar corte" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {cortes.map((c) => (
                                            <SelectItem key={c.id} value={c.id}>{c.relacion_corte} - {c.marca}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Quién Entrega *</Label>
                            <Input
                                value={entregaForm.entrega_nombre}
                                onChange={(e) => setEntregaForm({ ...entregaForm, entrega_nombre: e.target.value })}
                                placeholder="Nombre de quien entrega"
                                data-testid="entrega-nombre"
                            />
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Observaciones</Label>
                            <Textarea
                                value={entregaForm.observaciones}
                                onChange={(e) => setEntregaForm({ ...entregaForm, observaciones: e.target.value })}
                                placeholder="Notas adicionales"
                                rows={2}
                                data-testid="entrega-observaciones"
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setEntregaModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="save-entrega-btn">
                                Registrar Entrega
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Retorno Modal */}
            <Dialog open={retornoModalOpen} onOpenChange={setRetornoModalOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="retorno-modal">
                    <DialogHeader>
                        <DialogTitle >
                            <ArrowLeft className="w-5 h-5 inline mr-2 text-emerald-400" />
                            Registrar Retorno de Maquila
                        </DialogTitle>
                    </DialogHeader>
                    {selectedEntrega && (
                        <div className="bg-zinc-800 p-3 rounded-lg mb-4">
                            <p className="text-sm text-zinc-400">Maquilador: <span className="font-medium text-zinc-100">{selectedEntrega.maquilador_nombre}</span></p>
                            <p className="text-sm text-zinc-400">Cantidad entregada: <span className="font-medium text-zinc-100">{selectedEntrega.cantidad_entregada}</span></p>
                        </div>
                    )}
                    <form onSubmit={handleRetornoSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Fecha *</Label>
                                <Input
                                    type="date"
                                    value={retornoForm.fecha}
                                    onChange={(e) => setRetornoForm({ ...retornoForm, fecha: e.target.value })}
                                    data-testid="retorno-fecha"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Estado del Trabajo *</Label>
                                <Select value={retornoForm.estado_trabajo} onValueChange={(v) => setRetornoForm({ ...retornoForm, estado_trabajo: v })}>
                                    <SelectTrigger data-testid="retorno-estado">
                                        <SelectValue placeholder="Seleccionar" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ESTADOS_TRABAJO.map((e) => (
                                            <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Cantidad Regresada *</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={retornoForm.cantidad_regresada}
                                    onChange={(e) => {
                                        const regresada = parseInt(e.target.value) || 0;
                                        const faltante = selectedEntrega ? selectedEntrega.cantidad_entregada - regresada : 0;
                                        setRetornoForm({ ...retornoForm, cantidad_regresada: regresada, cantidad_faltante: Math.max(0, faltante) });
                                    }}
                                    data-testid="retorno-cantidad"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Cantidad Faltante</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={retornoForm.cantidad_faltante}
                                    readOnly
                                    className="bg-zinc-800"
                                    data-testid="retorno-faltante"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Quién Recibe *</Label>
                            <Input
                                value={retornoForm.recibe_nombre}
                                onChange={(e) => setRetornoForm({ ...retornoForm, recibe_nombre: e.target.value })}
                                placeholder="Nombre de quien recibe"
                                data-testid="retorno-recibe"
                            />
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Incidencias</Label>
                            <Textarea
                                value={retornoForm.incidencias}
                                onChange={(e) => setRetornoForm({ ...retornoForm, incidencias: e.target.value })}
                                placeholder="Describa cualquier incidencia"
                                rows={2}
                                data-testid="retorno-incidencias"
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setRetornoModalOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600" data-testid="save-retorno-btn">
                                Registrar Retorno
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
